import React, { useState } from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import type { MainAppPageProps } from "../types";

type DeleteAccountSectionProps = Pick<MainAppPageProps, "csrfToken" | "routes">;

export const DeleteAccountSection: React.FC<DeleteAccountSectionProps> = ({
  csrfToken,
  routes,
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [acknowledged, setAcknowledged] = useState(false);

  const action = routes.destroyAccount ?? "/dashboard/account";

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setStep(1);
      setAcknowledged(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="destructive"
        onClick={() => handleOpenChange(true)}
      >
        Delete account
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {step === 1 ? "Delete your account?" : "Confirm deletion"}
            </DialogTitle>
            <DialogDescription>
              {step === 1
                ? "This is irreversible. You will have to create a new account if you want to use ToolKit again."
                : "Check the box below to confirm you understand this cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          {step === 1 ? (
            <>
              <p className="text-sm text-muted-foreground">
                Job sites, photos, and other company content you added will stay
                with the company. Your login, name, and personal credentials
                will be removed.
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setStep(2)}
                >
                  Continue
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form action={action} method="post" className="flex flex-col gap-4">
              <AuthenticityTokenField csrfToken={csrfToken} />
              <input type="hidden" name="_method" value="delete" />
              <input type="hidden" name="confirm" value="1" />
              <label className="flex items-start gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
                <span>
                  I understand this is irreversible and I will have to create a
                  new account.
                </span>
              </label>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={!acknowledged}
                >
                  Delete my account
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
